import React from 'react';
import { TextArea, TextField, Text, Select } from '@chatsdet/ui';
import type {
  ConditionPredicate,
  ValidationOperator,
  WorkflowInputDefinition,
  WorkflowValueInputDefinition,
} from '@chatsdet/types';
import { useOrgCredentialTemplates } from '../../../hooks/useCredentialTemplates';

type ConditionNodeConfigProps = {
  focusKey: number;
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  workflowInputs?: WorkflowInputDefinition[];
};

const ACTING_AS_KEY = '__acting_as__';

/** Operator → human label. */
const OPERATOR_LABELS: Record<ValidationOperator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  gt: 'greater than',
  lt: 'less than',
  gte: 'greater than or equal',
  lte: 'less than or equal',
  contains: 'contains',
  not_contains: 'does not contain',
  is_set: 'is set',
  is_not_set: 'is not set',
};

const UNARY_OPERATORS = new Set<ValidationOperator>(['is_set', 'is_not_set']);

/** Type-appropriate operators for a value-input subject. */
function operatorsForInput(def: WorkflowValueInputDefinition | undefined): ValidationOperator[] {
  if (def?.type === 'number') return ['equals', 'not_equals', 'gt', 'lt', 'gte', 'lte'];
  // text / email / select / default
  return ['equals', 'not_equals', 'contains', 'not_contains', 'is_set', 'is_not_set'];
}

const ACTING_AS_OPERATORS: ValidationOperator[] = ['equals', 'not_equals'];

const VALID_OPERATORS = new Set<ValidationOperator>(
  Object.keys(OPERATOR_LABELS) as ValidationOperator[]
);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isUsableConditionPredicate(value: unknown): value is ConditionPredicate {
  if (!isRecord(value)) return false;
  if (typeof value.operator !== 'string') return false;
  if (!VALID_OPERATORS.has(value.operator as ValidationOperator)) return false;

  const subject = value.subject;
  if (!isRecord(subject)) return false;
  if (subject.kind === 'acting_as') return true;

  return subject.kind === 'variable' && typeof subject.name === 'string' && subject.name !== '';
}

export function inferConditionMode(localData: Record<string, unknown>): 'typed' | 'agent' {
  if (isUsableConditionPredicate(localData.predicate)) return 'typed';

  const conditionDescription =
    typeof localData.conditionDescription === 'string' ? localData.conditionDescription.trim() : '';
  return conditionDescription ? 'agent' : 'typed';
}

export const ConditionNodeConfig: React.FC<ConditionNodeConfigProps> = ({
  focusKey,
  localData,
  setLocalData,
  workflowInputs,
}) => {
  const conditionTextAreaRef = React.useRef<HTMLTextAreaElement>(null);

  const predicate = localData.predicate as ConditionPredicate | undefined;
  const valueInputs = React.useMemo(
    () =>
      (workflowInputs ?? []).filter(
        (d): d is WorkflowValueInputDefinition => d.kind === 'value'
      ),
    [workflowInputs]
  );
  const templatesQ = useOrgCredentialTemplates();
  const templateItems = React.useMemo(
    () => (templatesQ.data ?? []).map((t) => ({ id: t.id, label: t.name })),
    [templatesQ.data]
  );

  // Default a brand-new node (no description, no predicate) to typed mode so the
  // deterministic comparison is discoverable; keep existing agentic nodes as-is.
  const [mode, setMode] = React.useState<'typed' | 'agent'>(() =>
    inferConditionMode(localData)
  );

  React.useEffect(() => {
    setMode(inferConditionMode(localData));
    // Only reset when NodeConfigPanel loads a different selected node. Local
    // edits in the current node should not fight explicit mode selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  // Auto-focus the description textarea in agent mode when empty.
  React.useEffect(() => {
    if (mode !== 'agent') return;
    const condition = ((localData.conditionDescription as string | undefined) ?? '').trim();
    if (!condition) {
      const timer = setTimeout(() => conditionTextAreaRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [focusKey, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPredicate = (next: ConditionPredicate | undefined) =>
    setLocalData({ ...localData, predicate: next });

  const switchMode = (next: 'typed' | 'agent') => {
    setMode(next);
    if (next === 'typed' && !predicate) {
      const first = valueInputs[0];
      setPredicate({
        subject: first ? { kind: 'variable', name: first.name } : { kind: 'acting_as' },
        operator: 'equals',
        value: '',
      });
    } else if (next === 'agent') {
      setPredicate(undefined);
    }
  };

  // ── Predicate field derivations ──
  const subject = predicate?.subject;
  const subjectKey =
    subject?.kind === 'acting_as'
      ? ACTING_AS_KEY
      : subject?.kind === 'variable'
        ? subject.name
        : null;
  const selectedInput =
    subject?.kind === 'variable' ? valueInputs.find((d) => d.name === subject.name) : undefined;
  const isActingAs = subject?.kind === 'acting_as';
  const availableOperators = isActingAs
    ? ACTING_AS_OPERATORS
    : operatorsForInput(selectedInput);
  const needsValue = predicate ? !UNARY_OPERATORS.has(predicate.operator) : false;

  const subjectItems = [
    ...valueInputs.map((d) => ({ id: d.name, label: d.label || d.name })),
    { id: ACTING_AS_KEY, label: 'Acting as (login identity)' },
  ];

  const onSubjectChange = (key: string) => {
    // No early-return on a missing predicate: an existing condition can open in
    // typed mode with no predicate yet, and the first subject pick must create one.
    if (key === ACTING_AS_KEY) {
      setPredicate({ subject: { kind: 'acting_as' }, operator: 'equals', value: '' });
    } else {
      const def = valueInputs.find((d) => d.name === key);
      const ops = operatorsForInput(def);
      const operator = predicate && ops.includes(predicate.operator) ? predicate.operator : 'equals';
      setPredicate({
        subject: { kind: 'variable', name: key },
        operator,
        value: predicate?.value ?? '',
      });
    }
  };

  const onOperatorChange = (op: ValidationOperator) => {
    if (!predicate) return;
    setPredicate({ ...predicate, operator: op });
  };

  const onValueChange = (value: string) => {
    if (!predicate) return;
    setPredicate({ ...predicate, value });
  };

  const renderValueField = () => {
    if (!predicate || !needsValue) return null;

    if (isActingAs) {
      return (
        <Select
          label="Persona"
          items={templateItems}
          placeholder={templateItems.length ? 'Select persona...' : 'No personas available'}
          selectedKey={predicate.value || null}
          onSelectionChange={(k) => onValueChange(k as string)}
        />
      );
    }

    if (selectedInput?.type === 'select' && selectedInput.options) {
      const items = selectedInput.options.map((o) => ({ id: o.value, label: o.label }));
      return (
        <Select
          label="Value"
          items={items}
          placeholder="Select value..."
          selectedKey={predicate.value || null}
          onSelectionChange={(k) => onValueChange(k as string)}
        />
      );
    }

    return (
      <TextField
        label="Value"
        placeholder={selectedInput?.type === 'number' ? 'e.g. 10' : 'Value to compare against'}
        inputMode={selectedInput?.type === 'number' ? 'decimal' : undefined}
        value={predicate.value ?? ''}
        onChange={onValueChange}
      />
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <Select
          label="How should this condition be decided?"
          items={[
            { id: 'typed', label: 'Typed comparison (deterministic)' },
            { id: 'agent', label: 'Describe for the agent' },
          ]}
          selectedKey={mode}
          onSelectionChange={(k) => switchMode(k as 'typed' | 'agent')}
        />
        <Text size="xs" className="mt-1 text-muted-foreground">
          {mode === 'typed'
            ? 'Branches on a variable or the current login — evaluated instantly, no agent.'
            : 'The agent reads the page and decides true or false.'}
        </Text>
      </div>

      {mode === 'typed' ? (
        <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
          <Select
            label="Compare"
            items={subjectItems}
            placeholder={valueInputs.length ? 'Select...' : 'Acting as (login identity)'}
            selectedKey={subjectKey}
            onSelectionChange={(k) => onSubjectChange(k as string)}
          />
          <Select
            label="Operator"
            items={availableOperators.map((op) => ({ id: op, label: OPERATOR_LABELS[op] }))}
            selectedKey={predicate?.operator ?? null}
            onSelectionChange={(k) => onOperatorChange(k as ValidationOperator)}
          />
          {renderValueField()}
          {isActingAs && (
            <Text size="xs" className="text-muted-foreground">
              Matches when the persona established by the most recent login equals the selected one.
            </Text>
          )}
        </div>
      ) : (
        <>
          <TextArea
            ref={conditionTextAreaRef}
            label="Condition"
            placeholder="e.g., Does the user have any existing orders?"
            value={localData.conditionDescription || ''}
            onChange={(value) => setLocalData({ ...localData, conditionDescription: value })}
            description="Describe the condition to evaluate. The agent will determine if this is true or false."
            rows={2}
            autoExpand
          />
          <TextArea
            label="Evaluation Hints (optional)"
            placeholder="e.g., Look for order items in the table, check for 'No orders' message"
            value={localData.evaluationHints || ''}
            onChange={(value) => setLocalData({ ...localData, evaluationHints: value })}
            description="Optional hints to help the agent evaluate the condition accurately."
            rows={2}
            autoExpand
          />
        </>
      )}

      {mode === 'typed' && valueInputs.length === 0 && (
        <Text size="sm" className="text-muted-foreground">
          Tip: define workflow inputs (in the Inputs panel) to compare their values here, or compare
          the current “Acting as” login identity.
        </Text>
      )}
    </div>
  );
};
